import React from 'react';
import { Building2, Award, Mountain, Users } from 'lucide-react';

const AboutPPSRNP = () => {
  const sections = [
    {
      id: 'pamo',
      title: 'PPSRNP-PAMO',
      icon: Building2,
      description: 'The Puerto Princesa Subterranean River National Park–Protected Area Management Office (PPSRNP-PAMO) serves as the implementing and administrative arm of the park. It is responsible for executing approved plans, policies, and the day-to-day management and operations of the PPSRNP.',
      content: 'The PAMO is led by the Protected Area Superintendent (PASu) and is composed of officers covering Administration, Biodiversity Conservation and Protection, and Tourism Management. As the frontline management body, the PPSRNP-PAMO ensures effective implementation of conservation programs by responding to community concerns and overseeing operational activities that uphold the park\'s mandate for protection, sustainable use, and responsible stewardship of its natural and cultural resources.'
    },
    {
      id: 'unesco',
      title: 'UNESCO World Heritage Site',
      icon: Award,
      year: '1999',
      description: 'The Puerto Princesa Underground River was officially inscribed as a UNESCO World Heritage Site on December 4, 1999. It is one of the longest navigable underground rivers in the world, flowing directly into the sea.',
      content: 'It is rich in biodiversity, home to many endemic and endangered species, and features impressive cave formations like stalactites and stalagmites. Outstanding natural beauty, scientific value, and ecological significance, it is also an important example of environmental conservation and was named one of the New 7 Wonders of Nature. Through this recognition, UNESCO helps to ensure the conservation, protection, and sustainable management of PPUR for present and future generations.'
    },
    {
      id: 'geological',
      title: 'National Geological Monument',
      icon: Mountain,
      year: '2003',
      description: 'The St. Paul Limestone Formation is nested within the Puerto Princesa Subterranean River National Park (PPSRNP). It was declared as a National Geological Monument by the National Committee on Geological Sciences on December 4, 2003 in recognition of its outstanding scientific importance.',
      content: 'The site serves as a natural laboratory for the study of geological processes, noted for its uniqueness, integrity, and high scenic value. The monument is significant for illustrating how natural forces such as rainfall, erosion, and tectonic movements have shaped the landscape over millions of years. It stands as a rare and well-preserved example of karst geology in the Philippines, offering valuable insights into Earth\'s long-term natural processes.'
    },
    {
      id: 'ramsar',
      title: 'RAMSAR Convention (Site No. 2084)',
      icon: Users,
      description: 'The Puerto Princesa Subterranean River National Park is one of the Ramsar sites in the Philippines. The Ramsar Convention or Convention on Wetlands is an inter-governmental treaty which provides for the framework in the conservation of wetlands and their resources.',
      pillars: [
        'Work towards the wise use of all their wetlands',
        'Designate suitable wetlands for the list of Wetlands of International Importance (the "Ramsar List") and ensure their effective management',
        'Cooperate internationally on transboundary wetlands, shared wetland systems and shared species'
      ]
    },
    {
      id: 'seven-wonders',
      title: 'One of the New Seven Wonders of Nature',
      icon: Award,
      year: '2012',
      description: 'The Puerto Princesa Subterranean River National Park is globally recognized as one of the New Seven Wonders of Nature after being nominated to the global campaign organized by the New7Wonders Foundation.',
      content: 'Renowned for its spectacular limestone karst landscape, rich biodiversity, and a unique underground river that flows directly into the sea, PPSRNP advanced through international public voting and was included among the final 28 global finalists. Through continued worldwide support, the site was voted as one of the New 7 Wonders of Nature on November 11, 2011, highlighting its global importance and strengthening efforts for its protection and conservation. In recognition of this historic achievement, President Benigno Aquino III officially declared November 11 as Puerto Princesa Underground River (PPUR) Day, pursuant to Proclamation No. 816.'
    }
  ];

  return (
    <section className="py-16 bg-gradient-to-b from-[var(--color-background)] to-[var(--color-card)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-[var(--color-primary)] dark:text-[var(--color-tertiary)] mb-4">
            About PPSRNP
          </h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
            Discover the management, accolades, and global recognition that make Puerto Princesa Subterranean River National Park a world-class protected area.
          </p>
        </div>

        {/* Sections Grid */}
        <div className="space-y-8">
          {sections?.map((section, index) => {
            const IconComponent = section?.icon;
            return (
              <div
                key={section?.id}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 border border-[var(--color-border)]"
              >
                <div className="flex items-start gap-6">
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-full bg-[var(--color-primary)]/10 dark:bg-[var(--color-tertiary)]/10 flex items-center justify-center">
                      <IconComponent className="w-8 h-8 text-[var(--color-primary)] dark:text-[var(--color-tertiary)]" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-2xl font-bold text-[var(--color-primary)] dark:text-[var(--color-tertiary)]">
                        {section?.title}
                      </h3>
                      {section?.year && (
                        <span className="px-3 py-1 bg-[var(--color-accent)]/20 text-[var(--color-accent)] dark:text-[var(--color-tertiary)] rounded-full text-sm font-semibold">
                          {section?.year}
                        </span>
                      )}
                    </div>

                    <p className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
                      {section?.description}
                    </p>

                    {section?.content && (
                      <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                        {section?.content}
                      </p>
                    )}

                    {section?.pillars && (
                      <div className="mt-4">
                        <p className="font-semibold text-[var(--color-primary)] dark:text-[var(--color-tertiary)] mb-3">
                          Three Pillars of the Convention:
                        </p>
                        <ol className="space-y-2">
                          {section?.pillars?.map((pillar, idx) => (
                            <li key={idx} className="flex items-start space-x-2">
                              <span className="text-[var(--color-accent)] dark:text-[var(--color-tertiary)] mt-1">•</span>
                              <span className="text-gray-700 dark:text-gray-300">{pillar}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default AboutPPSRNP;